<template>
    <div class="fixed-chart">
        <div class="chart-title is-flex is-flex-vcenter is-flex-hbetween px-3">
            <span>{{ $t('evolucao-vendas') }}</span>
            <div class="is-flex is-flex-vcenter" v-if="!isLoading">
                <button
                    class="botao-flexa"
                    type="button"
                    tag="button"
                    @click="indexMesSelecionado -= 1"
                    :disabled="indexMesSelecionado <= 0"
                >
                    <b-icon icon="chevron-left" size="is-small"> </b-icon>
                </button>
                <span class="mx-2">
                    {{ labelMesSelecionado }}
                </span>
                <button
                    class="botao-flexa"
                    type="button"
                    tag="button"
                    @click="indexMesSelecionado += 1"
                    :disabled="indexMesSelecionado >= months.length - 1"
                >
                    <b-icon icon="chevron-right" size="is-small"> </b-icon>
                </button>
            </div>
        </div>

        <div class="is-flex-wrap" id="evolucao-card" v-if="!isLoading">
            <div class="info-graph">
                <line-graph
                    ref="graph"
                    class="pt-2"
                    style="height: 400px"
                    :items="itensGrafico"
                    :x-axis="eixoHorizontal"
                    :painted="false"
                    :smoothing="false"
                    :line-thickness="1"
                    :tooltip-render="tooltipRenderFunction"
                    use-ml
                />
            </div>

            <div class="info-bars p-3">
                <progress-card
                    :value="(objetivoValorAtual / objetivoEsperado) * 100"
                    :title="$t('objetivo-vendas')"
                >
                    <div class="p-2 stat-info">
                        <div class="columns is-flex is-flex-vbottom">
                            <div class="column">
                                <span>
                                    {{ $t('atual') }}
                                    <span
                                        v-if="objetivoPercent"
                                        class="ml-2"
                                        :style="{ color: objetivoPercentProps.color }"
                                    >
                                        <b-icon :icon="objetivoPercentProps.icon" size="is-small" />
                                        {{ getDecimal(Math.abs(objetivoPercent), 0) }}%
                                    </span>
                                </span>
                                <br />
                                <span class="bigger"
                                    >R$ {{ getDecimal(objetivoValorAtual, 2) }}</span
                                >
                            </div>
                            <div class="column">
                                <span>{{ $t('restante') }}</span>
                                <br />
                                <span class="bigger">
                                    R$
                                    {{
                                        getDecimal(
                                            objetivoEsperado - objetivoValorAtual > 0
                                                ? objetivoEsperado - objetivoValorAtual
                                                : 0,
                                            2
                                        )
                                    }}
                                </span>
                            </div>
                            <div class="column bigger">
                                {{ $t('esperado') }}
                                <br />
                                R$ {{ getDecimal(objetivoEsperado, 2) }}
                            </div>
                        </div>
                    </div>
                </progress-card>

                <progress-card
                    class="mt-2"
                    :value="(ticketMedioAtual / ticketMedioEsperado) * 100"
                    :title="$t('ticket-medio')"
                >
                    <div class="p-2 stat-info">
                        <div class="columns is-flex is-flex-vbottom">
                            <div class="column">
                                <span>{{ $t('atual') }}</span>
                                <br />
                                <span class="bigger">R$ {{ getDecimal(ticketMedioAtual, 2) }}</span>
                            </div>
                            <div class="column">
                                <span>{{ $t('restante') }}</span>
                                <br />
                                <span class="bigger">
                                    R$
                                    {{
                                        getDecimal(
                                            ticketMedioEsperado - ticketMedioAtual > 0
                                                ? ticketMedioEsperado - ticketMedioAtual
                                                : 0,
                                            2
                                        )
                                    }}
                                </span>
                            </div>
                            <div class="column bigger">
                                {{ $t('esperado') }}
                                <br />
                                R$ {{ getDecimal(ticketMedioEsperado, 2) }}
                            </div>
                        </div>
                    </div>
                </progress-card>

                <progress-card
                    class="mt-2"
                    :value="(diasUteisDecorridos / diasUteisNoMes) * 100"
                    :title="$t('dias-uteis')"
                >
                    <div class="p-2 stat-info">
                        <div class="columns is-flex is-flex-vbottom">
                            <div class="column">
                                <span>{{ $t('decorrido') }}</span>
                                <br />
                                <span class="bigger"
                                    >{{ diasUteisDecorridos }} {{ $t('dia-s') }}</span
                                >
                            </div>
                            <div class="column">
                                <span>{{ $t('restante') }}</span>
                                <br />
                                <span class="bigger"
                                    >{{ diasUteisNoMes - diasUteisDecorridos }}
                                    {{ $t('dia-s') }}</span
                                >
                            </div>
                            <div class="column bigger">
                                <span>{{ $t('total') }}</span>
                                <br />
                                <span class="bigger">{{ diasUteisNoMes }} {{ $t('dia-s') }}</span>
                            </div>
                        </div>
                    </div>
                </progress-card>

                <div class="bar-information is-flex is-flex-hbetween mt-4">
                    <div>
                        <span>{{ $t('objetivo-diario') }}</span>
                        <br />
                        <span style="font-size: 14px">R$ {{ getDecimal(objetivoDiario, 2) }}</span>
                    </div>
                    <div>
                        <router-link
                            tag="button"
                            class="save-btn px-4"
                            :to="{
                                name: 'relatorio-vendas-resultado-objetivo-vendas',
                                params: {
                                    mesSelecionado,
                                    vendedores: vendedoresCombo,
                                    tiposPedido: tiposPedido,
                                },
                            }"
                        >
                            {{ $t('detalhar-dados') }}
                        </router-link>
                    </div>
                </div>
            </div>
        </div>
        <div class="loading-card is-flex-centered is-flex-wrap" v-else>
            <div class="has-text-centered">
                <svg class="loading">
                    <use :xlink:href="require('@/assets/img/icons/loader.svg') + '#layer'"></use>
                </svg>
                <div class="is-100">{{ $t('carregando') }}...</div>
            </div>
        </div>
    </div>
</template>

<script>
import CockpitVendasService from '@/services/CockpitVendasService';
import LineGraph from '@/components/shared/graph/LineGraph';
import ProgressCard from './ProgressCard';

export default {
    components: {
        LineGraph,
        ProgressCard,
    },
    props: {
        inicio: {
            type: String,
            default: '',
        },
        fim: {
            type: String,
            default: '',
        },
        filtros: {
            type: Array,
            default: () => [],
        },
        /**
         * O combo do filtro aplicado atualmente do resultado de vendedores
         * Serve para poder aplicar no combo do relatório de resultado por
         * objetivo de vendas ao clicar em "Detalhar Dados"
         *
         * O que está no combo pode ser diferente dos vendedores que foram
         * utilizados para trazer o resultado atual, então para manter a consistência
         * dos valores ao trocar de tela, mantêm-se esse array com os vendedores
         * que foram utilizados para gerar este resultado.
         */
        vendedoresCombo: {
            type: Array,
            default: () => [],
        },
        tiposPedido: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            service: new CockpitVendasService(this.$http, this.$router),
            indexMesSelecionado: 0,
            isLoading: false,
            /* Gráfico */
            eixoHorizontal: [],
            itensGrafico: [],
            /* Cards */
            objetivoDiario: 0,
            objetivoValorAtual: 0,
            objetivoPercent: 0,
            objetivoEsperado: 0,
            ticketMedioAtual: 0,
            ticketMedioEsperado: 0,
            diasUteisNoMes: 0,
            diasUteisDecorridos: 0,
            /* Cores */
            greenAlert: '#4CAF50',
            redAlert: '#F44336',
        };
    },
    computed: {
        /* Gera um array de meses entre a data de início e a data de fim */
        months() {
            let dtInicio = this.inicio
                ? this.$moment(this.inicio, 'YYYY-MM-DD')
                : this.$moment().subtract(1, 'month');
            let dtFim = this.fim ? this.$moment(this.fim, 'YYYY-MM-DD') : this.$moment();

            let meses = [];

            while (dtInicio.isSameOrBefore(dtFim, 'month')) {
                meses.push({ mes: dtInicio.month() + 1, ano: dtInicio.year() });
                dtInicio.add(1, 'month');
            }

            return meses;
        },
        /**
         * Somente o índice do mês selecionado atualmente é salvo.
         * Este computed irá trazer o objeto de mẽs atual
         */
        mesSelecionado() {
            if (this.indexMesSelecionado > 0 && this.indexMesSelecionado < this.months.length) {
                return this.months[this.indexMesSelecionado];
            } else {
                return this.months[0];
            }
        },
        /* Gera a label do mẽs selecionado para exibir lá em cima */
        labelMesSelecionado() {
            if (this.mesSelecionado) {
                let { mes, ano } = this.mesSelecionado;
                return `${`0${mes}`.slice(-2)}/${ano}`;
            } else {
                return '';
            }
        },
        objetivoPercentProps() {
            if (this.objetivoPercent != null) {
                let props = {};

                if (this.objetivoPercent > 0) {
                    props.color = this.greenAlert;
                    props.icon = 'arrow-up';
                } else if (this.objetivoPercent < 0) {
                    props.color = this.redAlert;
                    props.icon = 'arrow-down';
                }

                return props;
            } else {
                return null;
            }
        },
    },
    watch: {
        /**
         * Watcher para quanto trocar os meses.
         * Se o mẽs de hoje estiver disponível, troca para ele.
         * Do contrário, pega o primeiro mẽs disponível.
         */
        months() {
            this.verificarMeses();
        },
        indexMesSelecionado(newIndex) {
            this.loadData();
        },
        filtros: {
            deep: true,
            handler: function () {
                this.loadData();
            },
        },
    },
    created() {
        this.verificarMeses();
        this.loadData();
    },
    methods: {
        /**
         * Carrega os dados do servidor e monta a estrutura para gerar o gráfico.
         */
        loadData() {
            this.isLoading = true;

            let { mes, ano } = this.months[this.indexMesSelecionado];

            /* No server, empresas e vendedores chegam como List<Long> */
            let filtroEnvio = this.getFiltroServer(this.filtros);

            this.service
                .getEvolucaoVendas(filtroEnvio, mes, ano)
                .then((r) => {
                    let eixo = this.getEixoMesSelecionado();

                    let itens = [
                        {
                            label: 'previsao-venda',
                            color: '#FF9800', // Orange Alert
                            values: r.previsaoVenda,
                        },
                        {
                            label: 'objetivo-venda',
                            values: r.objetivoVenda,
                        },
                        {
                            label: 'acumulado-diaria',
                            color: '#4CAF50', // Green Alert
                            values: r.acumuladoDiario,
                            painted: true,
                            gradient: true,
                            verticalLine: true,
                        },
                        {
                            label: 'mes-passado',
                            color: '#F44336', // Red Alert
                            values: r.mesPassado,
                        },
                        {
                            label: 'ano-passado',
                            color: '#FFEB3B', // Sovis Lime
                            values: r.anoPassado,
                        },
                    ];

                    this.eixoHorizontal = eixo;
                    this.itensGrafico = itens;

                    this.objetivoDiario = r.objetivoDiario;
                    this.objetivoValorAtual = r.objetivoValorAtual;
                    this.objetivoEsperado = r.objetivoEsperado;
                    this.ticketMedioAtual = r.ticketMedioAtual;
                    this.ticketMedioEsperado = r.ticketMedioEsperado;
                    this.diasUteisNoMes = r.diasUteisNoMes;
                    this.diasUteisDecorridos = r.diasUteisDecorridos;

                    let indexAtual = r.acumuladoDiario.length - 1;

                    if (indexAtual > -1 && r.objetivoVenda.length > 0) {
                        let objetivoDia = r.objetivoVenda[indexAtual];
                        this.objetivoPercent = (r.objetivoValorAtual / objetivoDia - 1) * 100;
                    } else {
                        this.objetivoPercent = null;
                    }

                    this.$emit('update-content', r);
                })
                .catch((e) => {
                    console.error('Erro ao carregar evolução de vendas', e);
                })
                .finally((_) => {
                    this.isLoading = false;
                });
        },
        /**
         * Função que retorna o HTML para exibir no tooltip.
         * data irá conter a label do eixo que está sendo exibido atualmente
         * e os itens naquela posição, ex: Ao passar o mouse em cima do dia 03:
         * {
         *      "xAxis": "03 ",
         *      "items": [
         *          {
         *              "label": "Mês Passado",
         *              "color": "#F44336",
         *              "value": 6680.15
         *          }
         *      ]
         * }
         */
        tooltipRenderFunction(data) {
            let html = '';
            let square = '13px';

            let { mes, ano } = this.mesSelecionado;

            let diaAtual = this.$moment([ano, mes - 1, parseInt(data.xAxis)]);
            let dow = diaAtual.format('dddd');
            let hasItems = data.items.some((e) => e.value != undefined);

            html = html.concat(`<span>${diaAtual.format('L')} - ${dow}</span>`);

            if (hasItems) {
                html = html.concat(`<table class="mt-2" style="border: none"><tbody>`);

                data.items.forEach((item) => {
                    if (item.value !== undefined) {
                        html = html.concat(
                            `<tr>
                                    <td style="border: none; vertical-align: middle;"><div style="width: ${square}; height: ${square}; background-color: ${
                                item.color
                            };"></div></td>
                                    <td class="pl-1" style="border: none">${this.$t(
                                        item.label
                                    )}</td>
                                    <td class="pl-4" style="border: none;">R$</td>
                                    <td style="border: none; text-align: right;">${this.getDecimal(
                                        item.value,
                                        2
                                    )}</td>
                                </tr>`
                        );
                    }
                });

                html = html.concat(`</tbody></table>`);
            }

            return html;
        },
        /**
         * Retorna o array de labels do mês selecionado
         * O espaço após a label é necessário para o d3 não se perder
         */
        getEixoMesSelecionado() {
            let d = [];
            let { mes, ano } = this.mesSelecionado;

            /* Cria moment no dia 1 do mês/ano selecionado */
            let moment = this.$moment([ano, mes - 1, 1]);

            for (let i = 0; i < moment.daysInMonth(); i++) {
                d.push(`0${i + 1} `.slice(-3));
            }

            return d;
        },
        /**
         * Método para verificar se o mês atual está incluído no período selecionado
         * Se estiver, coloca o mês de hoje como o selecionado
         * Do contrário usa o primeiro mês do período
         */
        verificarMeses() {
            let now = this.$moment();

            let mes = now.month() + 1;
            let ano = now.year();

            let index = this.months.findIndex((e) => e.mes == mes && e.ano == ano);

            if (index > -1) {
                this.indexMesSelecionado = index;
            } else {
                this.indexMesSelecionado = 0;
            }
        },
        /**
         * Obter o filtro para enviar para o servidor no loadData
         */
        getFiltroServer() {
            let filtros = {};

            this.filtros.forEach((f) => {
                switch (f.tipo) {
                    case 'tipo-analise':
                        filtros.tipoAnalise = f.valor;
                        break;
                    case 'empresa':
                        filtros.empresas = f.itens.map((e) => (typeof e == 'number' ? e : e.id));
                        break;
                    case 'vendedor':
                        filtros.vendedores = f.itens.map((e) => (typeof e == 'number' ? e : e.id));
                        break;
                    case 'tipo-pedido':
                        filtros.tiposPedido = f.itens.map((e) => (typeof e == 'number' ? e : e.id));
                        break;
                    case 'data-inicio':
                        filtros.datainicio = f.valor;
                        break;
                    case 'data-fim':
                        filtros.datafim = f.valor;
                        break;
                }
            });

            return filtros;
        },
    },
};
</script>

<style lang="scss" scoped>
.botao-flexa {
    background-color: var(--primary-color);
    color: #fff;
    border: 0;
    border-radius: 5px;
    &:disabled {
        background-color: #999;
    }
}
.fixed-chart {
    background: #fff;
    box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.1);

    .chart-title {
        height: 40px;
        font-size: 12px;
        font-weight: bold;
        color: $text-color;
        border-bottom: 1px solid $table-border;
    }

    #evolucao-card {
        display: flex;

        .info-graph {
            width: 60%;
        }

        .info-bars {
            width: 40%;
            display: flex;
            flex-flow: column;
            justify-content: space-evenly;

            .stat-info {
                font-size: 12px;
                font-weight: bold;
                padding-top: 0;

                .bigger {
                    font-size: 14px;

                    .bigger {
                        font-size: 16px;
                    }
                }
            }

            .bar-information {
                font-size: 12px;
                font-weight: bold;
                color: $text-color;
            }
        }
    }

    .loading-card {
        display: flex;
        height: 400px;
        color: $text-color;
        font-weight: bold;

        svg.loading {
            fill: $text-color;
            width: 50px !important;
            height: 50px !important;
            animation-name: spin;
            animation-duration: 2000ms;
            animation-iteration-count: infinite;
            animation-timing-function: linear;
        }
    }

    @media only screen and (max-width: 1024px) {
        #evolucao-card {
            .info-graph {
                width: 100%;
            }

            .info-bars {
                width: 100%;
            }
        }
    }

    @media only screen and (min-width: 1025px) {
        #evolucao-card {
            .info-bars {
                padding-left: 0;
            }
        }
    }
}
</style>
